import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Divider, Link, List, ListItem, Typography } from "@mui/material";
import FormDialog from "@components/FormDialog";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "@context/SnackBarProvider";
import { useMutation } from "@tanstack/react-query";
import { useDropzone } from "react-dropzone";
import {
  createBulkDriver,
  driverBulkSchema,
  DriverBulkFormData,
} from "@lib/api/mutations/driver";
import { AxiosError } from "axios";
import { CreateBulkDriverResponse, DriverResponse } from "@lib/types/driver";
import useHandleError from "@lib/utils/errorHook";
import { File } from "lucide-react";

interface Props {
  uploadDriverDialogOpen: boolean;
  setUploadDriversDialogOpen: (open: boolean) => void;
  refetch?: () => void;
}

const CreateBulkDriver: React.FC<Props> = ({
  uploadDriverDialogOpen,
  setUploadDriversDialogOpen,
  refetch,
}) => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const handleError = useHandleError();

  const [failedCreations, setFailedCreations] = useState<
    CreateBulkDriverResponse["failed_creations"]
  >([]);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm<DriverBulkFormData>({
    resolver: zodResolver(driverBulkSchema),
  });

  const { mutate, isPending: isCreatingDriver } = useMutation({
    mutationFn: createBulkDriver,
    onSuccess: (data: CreateBulkDriverResponse) => {
      if (data.failed_creations.length > 0) {
        setFailedCreations(data.failed_creations);
      } else {
        showSnackbar(
          t("user.drivers.bulkSuccess", {
            created_count: data.successful_creations.length,
          }),
          "success"
        );
        setUploadDriversDialogOpen(false);
        reset();
        refetch && refetch();
      }
    },
    onError: (error: AxiosError | unknown) => {
      if (error instanceof AxiosError && error.response) {
        const errorDetails = handleError(error.response.data);
        errorDetails && showSnackbar(errorDetails.message, "error");
      } else {
        showSnackbar(t("general.error"), "error");
      }
    },
  });

  const handleAddDriver = (data: DriverBulkFormData) => {
    mutate(data);
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setValue("file", acceptedFiles[0]); // Update the form value
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  return (
    <FormDialog
      title={t("user.drivers.add")}
      open={uploadDriverDialogOpen}
      onClose={() => {
        setUploadDriversDialogOpen(false);
        setFailedCreations([]);
        reset();
      }}
      onSubmit={handleSubmit(handleAddDriver)}
      submitText={t("general.save")}
      isSubmitting={isCreatingDriver}
      submittingText={t("general.saving")}
    >
      <form noValidate>
        <Box display="flex" flexDirection="column" gap={2} py={2}>
          <Typography
            variant="body1"
            color="textSecondary"
            mb={1}
            alignSelf={"center"}
          >
            <Link
              href="/assets/BulkUploadSample.csv"
              download
              underline="hover"
              color="primary"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <File size={16} /> {t("user.drivers.sampleFileLink")}
            </Link>
          </Typography>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <Box
                {...getRootProps()}
                sx={{
                  border: "2px dashed #ccc",
                  borderRadius: "8px",
                  padding: "16px",
                  textAlign: "center",
                  backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
                  cursor: "pointer",
                }}
              >
                <input
                  {...getInputProps({
                    name: field.name, // Pass the field name for proper integration
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.files?.length) {
                        field.onChange(e.target.files[0]); // Manually trigger react-hook-form update
                      }
                    },
                  })}
                />
                <Typography variant="body1" color="textSecondary">
                  {isDragActive
                    ? t("user.drivers.dropHere")
                    : t("user.drivers.dragDropOrClick")}
                </Typography>
                {field.value && (
                  <Typography variant="subtitle2" color="textPrimary" mt={1}>
                    {field.value.name}
                  </Typography>
                )}
              </Box>
            )}
          />

          {errors.file && (
            <Typography variant="body2" color="error">
              {errors.file.message}
            </Typography>
          )}

          {failedCreations.length > 0 && (
            <Box>
              <Typography variant="h6" color="error">
                {t("user.drivers.failedCreationsTitle")}
              </Typography>
              <List>
                {failedCreations.map((failed, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <Typography variant="body2">
                        <strong>{t("user.drivers.error")}</strong>:{" "}
                        {failed.error}
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Typography variant="body2">
                        <strong>{t("user.drivers.row")}</strong>:{" "}
                        {JSON.stringify(failed.row, null, 2)}
                      </Typography>
                    </ListItem>
                    {index < failedCreations.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </form>
    </FormDialog>
  );
};

export default CreateBulkDriver;
